import { clone } from 'ramda';
import { IPaymentMethodOrdering } from 'js/hooks/socket/types';
import { IPaymentMethodListItem } from 'js/hooks/socket/hooks/useGetPaymentMethodList/types';
import { IPaymentAccount, IPaymentMethodAccountsItem } from 'js/hooks/socket/hooks/useGetPaymentMethodAccounts/types';
import {
    CASHIER_TYPE,
    CASHIER_REDIRECT_TYPE,
    PAYMENT_METHODS_CODES
} from 'js/hooks/socket/constants';

import { getIsNuveiRealCard } from './cashierHelpers';
import { parsePaymentMethod } from './parsePaymentMethod';
import { CREDIT_CARD, SKIPPED_SUB_METHODS } from '../constants';
import { IAfterParsePaymentsList, IParsedPaymentMethod, IParsePaymentsList } from '../types';

export const sortByOrder = <Type extends { ordering?: IPaymentMethodOrdering }>(
    items: Type[],
    paymentMethodsOrder: keyof IPaymentMethodOrdering
): Type[] => items.sort((a, b) => {
    const aOrder = a.ordering?.[paymentMethodsOrder] || 999;
    const bOrder = b.ordering?.[paymentMethodsOrder] || 999;

    return aOrder - bOrder;
});

export const attachSubMethods = (
    methods: IPaymentMethodListItem[],
    paymentMethodsOrder: keyof IPaymentMethodOrdering
): IParsedPaymentMethod[] => Object.values(methods.reduce((acc, method) => {
    const { subMethods, ...parentMethod } = method;

    if (!subMethods) {
        return {
            ...acc,
            [method.code]: { ...method, isSubMethod: false }
        };
    }

    if (SKIPPED_SUB_METHODS.includes(method.code)) {
        return {
            ...acc,
            [method.code]: {
                ...method,
                isSubMethod: false,
                subMethods: {
                    subMethod: sortByOrder(subMethods.subMethod, paymentMethodsOrder)
                }
            }
        };
    }

    return {
        ...acc,
        ...subMethods.subMethod.reduce((subAcc, subMethod) => {
            const { code, name, ordering, reDirectType } = subMethod;

            return {
                ...subAcc,
                [subMethod.code]: {
                    ...parentMethod,
                    code,
                    name,
                    ordering,
                    reDirectType: reDirectType || method.reDirectType,
                    isSubMethod: true,
                    methodCode: method.code
                }
            };
        }, {}),
        [method.code]: { ...method, isSubMethod: false }
    };
}, {}));

export const filterCreditCards = (method: IPaymentMethodListItem, type: string): boolean => {
    const isNuveiAddCardMethod = method.code === PAYMENT_METHODS_CODES.NUVEI_CC
        && method.reDirectType === CASHIER_REDIRECT_TYPE.JS_NUVEI;

    if (isNuveiAddCardMethod && type === CASHIER_TYPE.WITHDRAWAL) {
        return false;
    }

    return getIsNuveiRealCard(method) || method.depositType !== CREDIT_CARD && method.withdrawType !== CREDIT_CARD;
};

export const filterParentMethods = (
    method: IPaymentMethodListItem,
    hideParentPaymentMethods: boolean
): boolean => !(hideParentPaymentMethods
    && method.hideParentMethod
    && method.subMethods
    && !SKIPPED_SUB_METHODS.includes(method.code));

export const attachAccounts = (
    method: IParsedPaymentMethod,
    accounts: IPaymentMethodAccountsItem[]
): IParsedPaymentMethod | null => {
    const accountRaw: IPaymentAccount[] = [];
    const pmAccount = accounts.find(acc => acc.code === method.code);

    pmAccount?.account && accountRaw.push(...pmAccount.account);

    accounts.forEach((account) => {
        account.account?.forEach((subAccount) => {
            if (subAccount.subMethods?.subMethod?.[0]?.code === method.code) {
                accountRaw.push(subAccount);
            }
        });
    });

    const account = accountRaw.filter(acc => acc.view === true && acc.specificParams);

    // Nuvei real CC methods should be available only when there are some accounts
    // because they do not have "Add account" option
    if (getIsNuveiRealCard(method) && !account.length) {
        return null;
    }

    return {
        ...method,
        ...pmAccount,
        account
    };
};

// For licensee - Custom filter to apply to payment methods or accounts
export const afterParsePaymentList = ({ parsedList }: IAfterParsePaymentsList): IParsedPaymentMethod[] => {
    return parsedList;
};

export const parsePaymentsList = (data: IParsePaymentsList): IParsedPaymentMethod[] => {
    const { type, methods: methodsData, accounts, hideParentPaymentMethods, paymentMethodsOrder } = data;
    const methods = clone(methodsData).filter(method => filterCreditCards(method, type));

    const paymentsList = attachSubMethods(methods, paymentMethodsOrder)
        .filter(method => filterParentMethods(method, hideParentPaymentMethods))
        .map(method => attachAccounts(method, accounts))
        .filter(Boolean)
        .map(method => parsePaymentMethod(method as IParsedPaymentMethod, type));

    return afterParsePaymentList({
        type,
        accounts,
        methods: methodsData,
        parsedList: sortByOrder(paymentsList, paymentMethodsOrder)
    });
};
